import "leaflet/dist/leaflet.css";
import L from "leaflet";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { MapContainer, useMap, useMapEvents } from "react-leaflet";

import { formatNumberBasedOnUser } from "../../utils/namesManipulation";
import { MapLayers } from "./MapLayers";

// Fix default icon issue in some configurations
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});
L.DomUtil.TRANSITION = true;
const createCircularIcon = (size, color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="background-color:${color}; width:${size}px; height:${size}px; border-radius:50%;"></div>`,
    iconAnchor: [size / 2, size / 2],
    iconSize: [size, size],
    popupAnchor: [0, -size / 2],
  });
};

const MapZoomToFit = ({ data }) => {
  const map = useMap();
  const [boundsSet, setBoundsSet] = useState(false);

  useEffect(() => {
    if (!boundsSet && data.length > 0) {
      const bounds = L.latLngBounds(data?.map((point) => [point.latitude, point.longitude]));
      map.fitBounds(bounds);
      setBoundsSet(true);
    }
  }, [JSON.stringify(data?.map((point) => [point.latitude, point.longitude])), boundsSet]);

  return null;
};

export const MyMap = React.memo(function MyMapFn({
  className,
  data,
  defaultLayer,
  eventHandlers,
  hasTooltip = true,
  rerenderVariable,
  resizeTriggerVariables = [],
  shouldCluster = true,
  ...props
}) {
  const noNullData = data?.map((point) => ({
    ...point,
    center: [point.latitude ?? 0, point.longitude ?? 0],
    latitude: point.latitude ?? 0,
    longitude: point.longitude ?? 0,
  }));
  const [_renderFlag, setRenderFlag] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(2);
  const MapZoomListener = () => {
    const map = useMapEvents({
      zoom: () => {
        setZoomLevel(map.getZoom());
      },
    });
    return null;
  };

  useEffect(() => {
    setRenderFlag((renderFlag) => !renderFlag);
  }, [rerenderVariable]);

  useEffect(() => {
    if (miniMapRef.current) miniMapRef.current.invalidateSize();
  }, [...resizeTriggerVariables]);

  const pointEventHandlers = (point) => {
    let output = {};
    if (!eventHandlers) return output;
    Object.entries(eventHandlers)?.forEach(([eventName, action]) => {
      output[eventName] = (...rest) => action(point, ...rest);
    });

    return output;
  };

  const latValues = noNullData?.map((point) => point.latitude);
  const lonValues = noNullData?.map((point) => point.longitude);

  latValues.sort((a, b) => a - b);
  lonValues.sort((a, b) => a - b);

  const centerIndex = Math.floor(noNullData?.length / 2);

  const center = [latValues[centerIndex] ?? 0, lonValues[centerIndex] ?? 0];

  const miniMapRef = useRef(null);

  const tilelayerProps = {
    maxNativeZoom: 19,
    maxZoom: 19,
  };

  return (
    <MapContainer
      center={center}
      className={className}
      key={rerenderVariable}
      maxZoom={tilelayerProps.maxZoom}
      minZoom={2}
      scrollWheelZoom={true}
      zoom={zoomLevel}
      {...props}
      ref={miniMapRef}
    >
      <MapZoomToFit data={noNullData} />
      <MapZoomListener />
      <MapLayers
        defaultLayer={defaultLayer === "Standard" ? "Standard" : "Basic"}
        layerProps={tilelayerProps}
        layers={["Basic", "Standard", "Satellite", "Dark", "Topographic"]}
      />
      <MarkerCluster
        data={noNullData}
        hasTooltip={hasTooltip}
        pointEventHandlers={pointEventHandlers}
        shouldCluster={shouldCluster}
      />
    </MapContainer>
  );
},
isEqual);

const MarkerCluster = ({ data, hasTooltip, pointEventHandlers, shouldCluster }) => {
  const map = useMap();
  useEffect(() => {
    const markers = L.markerClusterGroup({
      disableClusteringAtZoom: shouldCluster ? 9 : 0,
      maxClusterRadius: 5,
      spiderfyOnMaxZoom: false,
    });
    data?.forEach((point) => {
      const marker = L.marker(point.center, {
        icon: createCircularIcon(20, point.color),
      });

      if (hasTooltip)
        marker.bindTooltip(
          `
                    <div style="font-size:10px; padding: 5px; border-radius: 5px;">
                        <strong >${point.name || ""}</strong><br />
                        <span class="font-ds-number-regular-xs"> ${
                          formatNumberBasedOnUser(point.value) || "No value"
                        }</span>
                    </div>
            `,
          {
            direction: "top",
            permanent: false,
          }
        );

      const eventHandlers = pointEventHandlers(point);
      Object.entries(eventHandlers).forEach(([eventName, handler]) => {
        marker.on(eventName, () => {
          const pointXYCoordinates = map?.latLngToContainerPoint(point.center);
          const mapContainer = map.getContainer();
          const containerRect = mapContainer.getBoundingClientRect();
          const viewportPosition = {
            x: containerRect.left + pointXYCoordinates.x,
            y: containerRect.top + pointXYCoordinates.y,
          };
          handler(viewportPosition);
        });
      });
      markers.addLayer(marker);
    });

    map.addLayer(markers);

    return () => {
      map.removeLayer(markers);
    };
  }, [map, data, shouldCluster]);

  return null;
};
